import { useCallback, useEffect, useState } from 'react';
import { ApiError } from './api';

interface ApiState<T> {
  loading: boolean;
  data?: T;
  error?: string;
}

// Minimal data-fetch hook: runs `fn` on mount and when `deps` change, exposing
// loading/data/error + a reload(). Keeps the Phase-2 shell dependency-light (no react-query).
export function useApi<T>(fn: () => Promise<T>, deps: unknown[]): ApiState<T> & { reload: () => void } {
  const [state, setState] = useState<ApiState<T>>({ loading: true });

  const run = useCallback(() => {
    // Keep prior data during a refetch (SWR-style) so a reload() doesn't blank the view —
    // callers gate the full-screen loader on `loading && !data` (initial load only).
    setState((s) => ({ loading: true, data: s.data }));
    fn()
      .then((data) => setState({ loading: false, data }))
      .catch((e) => setState({ loading: false, error: e instanceof ApiError ? e.reason : 'error' }));
    // deps drive re-fetch; fn is intentionally not a dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(run, [run]);
  return { ...state, reload: run };
}
